import { useCallback, useEffect, useRef, useState } from "react";
import { SyncLoader } from "react-spinners";
import { ResourceInterface } from "../../domain/interface/resource_interface";
import ThemeConstants from "../../../../config/theme/theme_constants";
import PlayIcon from "../../../../assets/images/play_icon.svg?react";
import PauseIcon from "../../../../assets/images/pause_icon.svg?react";
import FullscreenIcon from "../../../../assets/images/fullscreen_icon.svg?react";
import FullscreenExitIcon from "../../../../assets/images/fullscreen_exit_icon.svg?react";
import SoundIcon from "../../../../assets/images/sound_icon.svg?react";
import MuteIcon from "../../../../assets/images/mute_icon.svg?react";
import { useTranslation } from "../../../../i18n/useTranslation";

interface Props {
  resource: ResourceInterface;
  onCompleted: (id: string) => void;
}

const formatTime = (s: number): string => {
  if (!isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

type WebkitVideo = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
  webkitDisplayingFullscreen?: boolean;
  webkitSupportsFullscreen?: boolean;
};

type WebkitElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

type WebkitDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};

const isIOS = (): boolean => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 1);
};

const SecureVideoPlayer: React.FC<Props> = ({ resource, onCompleted }) => {
  const { t } = useTranslation();
  const theme = ThemeConstants();
  const isUnlocked = resource.viewed === true;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);
  const maxTimeRef = useRef(isUnlocked ? Number.POSITIVE_INFINITY : 0);
  const hasCompletedRef = useRef(false);
  const cheatTimeoutRef = useRef<number | null>(null);
  const recoverCountRef = useRef(0);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [cheatNotice, setCheatNotice] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const [isBuffering, setIsBuffering] = useState(true);
  const [bufferedRanges, setBufferedRanges] = useState<[number, number][]>([]);
  const [isSeeking, setIsSeeking] = useState(false);
  const [isIOSDevice] = useState<boolean>(() => isIOS());
  const [layoutTick, setLayoutTick] = useState(0);

  const handleCheat = useCallback((reason: string) => {
    const v = videoRef.current;
    if (v) v.pause();
    setCheatNotice(reason);
    if (cheatTimeoutRef.current) window.clearTimeout(cheatTimeoutRef.current);
    cheatTimeoutRef.current = window.setTimeout(() => setCheatNotice(null), 2000);
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const onTimeUpdate = () => {
      if (!isUnlocked) {
        maxTimeRef.current = Math.max(maxTimeRef.current, v.currentTime);
      }
      setCurrentTime(v.currentTime);
    };
    const onSeeking = () => {
      setIsSeeking(true);
      if (isUnlocked) return;
      if (v.currentTime > maxTimeRef.current + 0.5) {
        v.currentTime = maxTimeRef.current;
        handleCheat(t('course.player.cheat'));
      }
    };
    const onRateChange = () => {
      if (isUnlocked) return;
      if (v.playbackRate > 1) {
        v.playbackRate = 1;
        handleCheat(t('course.player.speed_blocked'));
      }
    };
    const onEnterPiP = () => {
      if (isUnlocked) return;
      if (document.pictureInPictureElement) {
        document.exitPictureInPicture().catch(() => {});
      }
      handleCheat(t('course.player.pip_blocked'));
    };
    const onLoadedMetadata = () => setDuration(v.duration);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => {
      setIsPlaying(false);
      setIsSeeking(false);
    };
    const onEnded = () => {
      if (isUnlocked || hasCompletedRef.current) return;
      const tolerance = v.duration < 1 ? v.duration * 0.05 : 0.5;
      if (maxTimeRef.current >= v.duration - tolerance) {
        hasCompletedRef.current = true;
        onCompleted(resource._id);
      }
    };
    const onErr = () => {
      const err = v.error;
      // seek en arrière vers une zone évincée du buffer -> erreur réseau récupérable
      if (err && err.code === MediaError.MEDIA_ERR_NETWORK && recoverCountRef.current < 2) {
        recoverCountRef.current += 1;
        const resumeAt = v.currentTime;
        const wasPlaying = !v.paused;
        setIsBuffering(true);
        const restore = () => {
          v.removeEventListener("loadedmetadata", restore);
          try { v.currentTime = resumeAt; } catch { /* noop */ }
          if (wasPlaying) v.play().catch(() => {});
        };
        v.addEventListener("loadedmetadata", restore);
        v.load();
        return;
      }
      setHasError(true);
    };
    const onWaiting = () => setIsBuffering(true);
    const onLoadStart = () => setIsBuffering(true);
    const onCanPlay = () => setIsBuffering(false);
    const onPlaying = () => {
      setIsBuffering(false);
      setIsSeeking(false);
      recoverCountRef.current = 0;
    };
    const onProgress = () => {
      const ranges: [number, number][] = [];
      for (let i = 0; i < v.buffered.length; i++) ranges.push([v.buffered.start(i), v.buffered.end(i)]);
      setBufferedRanges(ranges);
    };
    const onVisibility = () => {
      if (isUnlocked) return;
      if (document.hidden) v.pause();
    };
    const forceRelayout = () => {
      const c = containerRef.current;
      if (!c) return;
      void c.offsetHeight;
      setLayoutTick((tick) => tick + 1);
    };
    const onFsChange = () => {
      const doc = document as WebkitDocument;
      const fsEl = document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
      const next = fsEl === containerRef.current;
      setIsFullscreen(next);
      if (!next) requestAnimationFrame(forceRelayout);
    };
    const onWebkitBeginFs = () => setIsFullscreen(true);
    const onWebkitEndFs = () => {
      setIsFullscreen(false);
      requestAnimationFrame(forceRelayout);
    };

    v.addEventListener("timeupdate", onTimeUpdate);
    v.addEventListener("seeking", onSeeking);
    v.addEventListener("ratechange", onRateChange);
    v.addEventListener("enterpictureinpicture", onEnterPiP);
    v.addEventListener("loadedmetadata", onLoadedMetadata);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("ended", onEnded);
    v.addEventListener("error", onErr);
    v.addEventListener("waiting", onWaiting);
    v.addEventListener("loadstart", onLoadStart);
    v.addEventListener("canplay", onCanPlay);
    v.addEventListener("playing", onPlaying);
    v.addEventListener("progress", onProgress);
    document.addEventListener("visibilitychange", onVisibility);
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    v.addEventListener("webkitbeginfullscreen", onWebkitBeginFs);
    v.addEventListener("webkitendfullscreen", onWebkitEndFs);

    return () => {
      v.removeEventListener("timeupdate", onTimeUpdate);
      v.removeEventListener("seeking", onSeeking);
      v.removeEventListener("ratechange", onRateChange);
      v.removeEventListener("enterpictureinpicture", onEnterPiP);
      v.removeEventListener("loadedmetadata", onLoadedMetadata);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("ended", onEnded);
      v.removeEventListener("error", onErr);
      v.removeEventListener("waiting", onWaiting);
      v.removeEventListener("loadstart", onLoadStart);
      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("playing", onPlaying);
      v.removeEventListener("progress", onProgress);
      document.removeEventListener("visibilitychange", onVisibility);
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
      v.removeEventListener("webkitbeginfullscreen", onWebkitBeginFs);
      v.removeEventListener("webkitendfullscreen", onWebkitEndFs);
      if (cheatTimeoutRef.current) window.clearTimeout(cheatTimeoutRef.current);
    };
  }, [resource._id, onCompleted, handleCheat, isUnlocked, t]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  }, []);

  const seekFromPointer = useCallback((clientX: number) => {
    const bar = barRef.current;
    const v = videoRef.current;
    if (!bar || !v || !duration) return;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const target = pct * duration;
    v.currentTime = isUnlocked ? target : Math.min(target, maxTimeRef.current);
  }, [duration, isUnlocked]);

  const onBarPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    seekFromPointer(e.clientX);
    const onMove = (ev: PointerEvent) => seekFromPointer(ev.clientX);
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, [seekFromPointer]);

  const handleVolumeChange = (val: number) => {
    setVolume(val);
    setIsMuted(val === 0);
    if (videoRef.current) {
      videoRef.current.volume = val;
      videoRef.current.muted = val === 0;
    }
  };

  const toggleMute = () => {
    const next = !isMuted;
    setIsMuted(next);
    if (videoRef.current) videoRef.current.muted = next;
  };

  const toggleFullscreen = () => {
    const container = containerRef.current as WebkitElement | null;
    const video = videoRef.current as WebkitVideo | null;
    const doc = document as WebkitDocument;
    const fsEl = document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;

    if (fsEl) {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      } else if (doc.webkitExitFullscreen) {
        doc.webkitExitFullscreen();
      }
      return;
    }

    if (video?.webkitDisplayingFullscreen) {
      video.webkitExitFullscreen?.();
      return;
    }

    if (container?.requestFullscreen) {
      container.requestFullscreen().catch(() => {
        video?.webkitEnterFullscreen?.();
      });
    } else if (container?.webkitRequestFullscreen) {
      try {
        container.webkitRequestFullscreen();
      } catch {
        video?.webkitEnterFullscreen?.();
      }
    } else if (video?.webkitEnterFullscreen) {
      video.webkitEnterFullscreen();
    }
  };

  const maxPct = duration ? (maxTimeRef.current / duration) * 100 : 0;
  const curPct = duration ? (currentTime / duration) * 100 : 0;

  if (hasError) {
    return (
      <div className="w-full min-h-[45dvh] flex items-center justify-center bg-black text-white">
        <span className="text-sm">{t('course.player.video_error')}</span>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`w-full ${isFullscreen ? "h-dvh" : "h-[45dvh]"} relative bg-black group ${isUnlocked ? "" : "select-none"}`}
      onContextMenu={(e) => e.preventDefault()}
    >
      <video
        ref={videoRef}
        src={resource.content}
        controls={false}
        disablePictureInPicture
        controlsList={isUnlocked ? "nodownload noremoteplayback" : "nodownload nofullscreen noremoteplayback"}
        playsInline
        className="absolute inset-0 w-full h-full object-contain"
        onClick={togglePlay}
      />

      {isBuffering && !isSeeking && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/20">
          <SyncLoader color="#ffffff" size={14} speedMultiplier={0.7} />
        </div>
      )}

      {!isPlaying && !isBuffering && (
        <div
          key={`overlay-${layoutTick}`}
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
        >
          <button
            className="pointer-events-auto cursor-pointer p-3 h-[28%] aspect-square text-white"
            onClick={togglePlay}
            aria-label={t('course.player.play')}
          >
            <PlayIcon className="h-full w-auto drop-shadow-lg" />
          </button>
        </div>
      )}

      {cheatNotice && (
        <div
          className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full text-white text-xs font-semibold shadow-md"
          style={{ background: theme.UvibesPink }}
        >
          {cheatNotice}
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 px-4 pb-3 pt-8 bg-linear-to-t from-black/80 to-transparent text-white flex flex-col gap-2">
        <div
          ref={barRef}
          className="relative h-2 w-full rounded-full bg-white/25 cursor-pointer"
          onPointerDown={onBarPointerDown}
        >
          {duration > 0 && bufferedRanges.map(([s, e], i) => (
            <div
              key={i}
              className="absolute top-0 h-full rounded-full bg-white/40"
              style={{ left: `${(s / duration) * 100}%`, width: `${((e - s) / duration) * 100}%` }}
            />
          ))}
          {!isUnlocked && (
            <div
              className="absolute top-0 left-0 h-full rounded-full bg-white/50"
              style={{ width: `${maxPct}%` }}
            />
          )}
          <div
            className="absolute top-0 left-0 h-full rounded-full"
            style={{ width: `${curPct}%`, background: theme.UvibesPink }}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white shadow"
            style={{ left: `calc(${curPct}% - 6px)` }}
          />
        </div>

        <div className="flex items-center gap-3 text-sm">
          <button onClick={togglePlay} className="cursor-pointer w-6 h-6 flex items-center justify-center" aria-label={isPlaying ? t('course.player.pause') : t('course.player.play')}>
            {isPlaying ? <PauseIcon className="w-5 h-5" /> : <PlayIcon className="w-5 h-5" />}
          </button>
          <span className="tabular-nums text-xs">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>

          <div className="flex items-center gap-2 ml-2">
            <button onClick={toggleMute} className="cursor-pointer w-6 h-6 flex items-center justify-center" aria-label={isMuted ? t('course.player.unmute') : t('course.player.mute')}>
              {isMuted || volume === 0 ? <MuteIcon className="w-5 h-5" /> : <SoundIcon className="w-5 h-5" />}
            </button>
            {!isIOSDevice && (
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={isMuted ? 0 : volume}
                onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                className="w-20 accent-white"
              />
            )}
          </div>

          <button onClick={toggleFullscreen} className="ml-auto cursor-pointer w-6 h-6 flex items-center justify-center" aria-label={isFullscreen ? t('course.player.fullscreen_exit') : t('course.player.fullscreen')}>
            {isFullscreen ? <FullscreenExitIcon className="w-5 h-5" /> : <FullscreenIcon className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SecureVideoPlayer;
